import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import StoryCard from '../components/StoryCard'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import SEO from '../components/SEO'
import renderMentions from '../lib/renderMentions'
import { decodeEntities } from '../lib/decodeEntities'
import SkeletonLoader from '../components/SkeletonLoader'
import PullToRefresh from '../components/PullToRefresh'
import { wallTimeAgo, WallComments } from '../components/WallPost'
import ReportModal from '../components/ReportModal'
import BackToTop from '../components/BackToTop'
import OnlineDot from '../components/OnlineDot'

// ─── Refresh icon (matches Navbar/BottomNav inline-SVG convention) ──────────

function RefreshIcon({ spinning }) {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={spinning ? 'feed-sidebar__refresh-icon--spinning' : undefined}
      aria-hidden="true"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  )
}

// ─── Author suggestion card ─────────────────────────────────────────────────

function AuthorCard({ author, onFollow, onUnfollow, onCancelRequest, followingSet, requestedMap }) {
  const isFollowing = followingSet.has(author.username)
  const requestEntry = requestedMap.get(author.username) // { requestId } | undefined
  const isRequested = !!requestEntry
  const [busy, setBusy] = useState(false)

  async function toggle() {
    setBusy(true)
    try {
      if (isFollowing)      await onUnfollow(author.username)
      else if (isRequested) await onCancelRequest(author.username, requestEntry.requestId)
      else                  await onFollow(author)
    } finally {
      setBusy(false)
    }
  }

  // Three-state label. Uses requiresFollowRequest from the /suggestions
  // response (Commit 2b) so privacy_mode targets show "Request to follow"
  // up-front rather than flipping after click. Matches the pattern on
  // UserProfile and StoryReader (deliberate copy per no-shared-component
  // decision in plan §A.4).
  let label
  if (busy)                           label = '…'
  else if (isFollowing)               label = '✓ Following'
  else if (isRequested)               label = '✓ Requested'
  else if (author.requiresFollowRequest) label = '+ Request to follow'
  else                                label = '+ Follow'
  const isInactive = isFollowing || isRequested

  return (
    <div className="feed-author-card">
      <Link to={`/user/${author.username}`} className="feed-author-card__left">
        <div className="feed-author-card__avatar" style={{ background: author.avatarUrl ? undefined : author.avatarGrad }}>
          {author.avatarUrl
            ? <img src={author.avatarUrl} alt={author.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            : (author.displayName[0] ?? '?').toUpperCase()}
        </div>
        <div className="feed-author-card__info">
          <span className="feed-author-card__name">{decodeEntities(author.displayName)}</span>
          <span className="feed-author-card__meta">
            @{author.username}
            {author.followers > 0 && ` · ${formatNum(author.followers)} followers`}
          </span>
          {author.tagline && (
            <span className="feed-author-card__tagline">{author.tagline}</span>
          )}
        </div>
      </Link>
      <button
        className={`btn feed-author-card__btn${isInactive ? ' btn-outline' : ' btn-primary'}`}
        onClick={toggle}
        disabled={busy}
      >
        {label}
      </button>
    </div>
  )
}

// ─── Online mutuals ─────────────────────────────────────────────────────────
// Single source of markup, rendered twice: in the sidebar on desktop and inline
// in the main column on mobile. `variant` only swaps the wrapper class so CSS
// (900px breakpoint) decides which copy is visible. No data fetch here — the
// list is owned by FeedPage state and passed in.

function OnlineMutuals({ mutuals, variant }) {
  if (!mutuals.length) return null
  const wrapperClass = variant === 'inline'
    ? 'feed-online--inline'
    : 'feed-sidebar__section feed-online--sidebar'
  return (
    <div className={wrapperClass}>
      <h2 className="feed-sidebar__title">Online now</h2>
      <div className="feed-online-list">
        {mutuals.map(m => (
          <Link key={m.id} to={`/user/${m.username}`} className="feed-online-row">
            <div className="feed-online-row__avatar" style={{ position: 'relative', background: m.avatarUrl ? undefined : m.avatarGrad }}>
              {m.avatarUrl
                ? <img src={m.avatarUrl} alt={m.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : (m.displayName[0] ?? '?').toUpperCase()}
              <OnlineDot userId={m.id} size={10} fallback />
            </div>
            <span className="feed-online-row__name">{decodeEntities(m.displayName)}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatNum(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
  if (n >= 1000)    return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  return session ? { Authorization: `Bearer ${session.access_token}` } : {}
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function FeedPage() {
  const { user } = useAuth()

  const [stories, setStories]           = useState([])
  const [total, setTotal]               = useState(0)
  const [followingCount, setFollowingCount] = useState(null)  // null = loading
  const [suggestions, setSuggestions]   = useState([])
  const [refreshingSuggestions, setRefreshingSuggestions] = useState(false)
  const [seenSuggestionIds, setSeenSuggestionIds] = useState(() => new Set())
  const [followingSet, setFollowingSet] = useState(new Set())
  const [requestedMap, setRequestedMap] = useState(() => new Map()) // username → { requestId }
  const [loading, setLoading]           = useState(true)
  const [loadingMore, setLoadingMore]   = useState(false)
  const [offset, setOffset]             = useState(0)
  const [continueReading, setContinueReading] = useState([])
  const [wallPosts, setWallPosts] = useState([])
  const [likedWallPosts, setLikedWallPosts] = useState(() => new Set())
  const [openComments, setOpenComments] = useState(() => new Set())
  const [reportingPostId, setReportingPostId] = useState(null)
  const [onlineMutuals, setOnlineMutuals] = useState([])
  const PAGE = 12

  const loadFeed = useCallback(async () => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const [feedRes, suggestRes, continueRes, wallRes, onlineRes] = await Promise.all([
        axios.get('/api/follows/feed', { params: { limit: PAGE, offset: 0 }, headers }),
        axios.get('/api/follows/suggestions', { params: { limit: 4 }, headers }),
        axios.get('/api/stories/continue-reading', { headers }).catch(err => {
          console.error('[FeedPage] continue-reading fetch failed:', err)
          return { data: [] }
        }),
        axios.get('/api/wall/feed', { params: { limit: 3 }, headers }).catch(err => {
          console.error('[FeedPage] wall feed fetch failed:', err)
          return { data: { posts: [] } }
        }),
        axios.get('/api/follows/mutuals/online', { headers }).catch(err => {
          console.error('[FeedPage] online mutuals fetch failed:', err)
          return { data: [] }
        }),
      ])
      setStories(feedRes.data.stories)
      setTotal(feedRes.data.total)
      setFollowingCount(feedRes.data.followingCount)
      setSuggestions(suggestRes.data)
      setOffset(feedRes.data.stories.length)
      setContinueReading(continueRes.data)
      const posts = wallRes.data.posts ?? []
      setWallPosts(posts)
      setLikedWallPosts(new Set(posts.filter(p => p.likedByMe).map(p => p.id)))
      setOnlineMutuals(onlineRes.data ?? [])
    } catch (err) {
      console.error('[FeedPage] Failed to load feed:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Load feed + suggestions + continue reading in parallel
  useEffect(() => {
    if (!user) { setLoading(false); return }
    loadFeed()
  }, [user, loadFeed])

  // Refresh the suggestions list — accumulates already-seen IDs and passes them
  // as ?exclude=... so the server can cycle through the pool on repeat clicks.
  // When the pool depletes (server returns 0 rows), reset the accumulator and
  // re-fetch the top 4. Keeps the stale list visible on network error so the
  // card never blanks out.
  const refreshSuggestions = useCallback(async () => {
    if (refreshingSuggestions) return
    setRefreshingSuggestions(true)
    try {
      const headers = await getHeaders()
      const excludeIds = new Set(seenSuggestionIds)
      for (const s of suggestions) excludeIds.add(s.id)

      let { data } = await axios.get('/api/follows/suggestions', {
        params: { limit: 4, exclude: [...excludeIds].join(',') },
        headers,
      })

      if (data.length === 0) {
        // Pool exhausted — reset and fall through to a fresh top-4 fetch.
        setSeenSuggestionIds(new Set())
        const retry = await axios.get('/api/follows/suggestions', { params: { limit: 4 }, headers })
        data = retry.data
      } else {
        setSeenSuggestionIds(excludeIds)
      }

      setSuggestions(data)
    } catch (err) {
      console.warn('[FeedPage] suggestions refresh failed:', err.message)
    } finally {
      setRefreshingSuggestions(false)
    }
    // Deps intentionally include `suggestions` and `seenSuggestionIds` — the
    // callback must re-identify on every refresh so the accumulation logic
    // sees the latest values. Do not "optimize" by dropping these; doing so
    // freezes the closure and breaks the cycle-through behavior.
  }, [refreshingSuggestions, suggestions, seenSuggestionIds])

  const loadMore = useCallback(async () => {
    if (loadingMore) return
    setLoadingMore(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.get('/api/follows/feed', { params: { limit: PAGE, offset }, headers })
      setStories(prev => [...prev, ...data.stories])
      setOffset(prev => prev + data.stories.length)
    } finally {
      setLoadingMore(false)
    }
  }, [offset, loadingMore])

  // `author` is the full suggestion row (includes requiresFollowRequest).
  // Server decides follows vs follow_requests based on target.privacy_mode;
  // response shape:
  //   { following: true }                        → privacy_mode=false
  //   { requested: true, requestId: <uuid> }     → privacy_mode=true
  async function follow(author) {
    const headers = await getHeaders()
    const res = await axios.post(`/api/follows/${author.username}`, {}, { headers })
    if (res.data.requested) {
      // Protected target — capture requestId so cancel can DELETE it later.
      setRequestedMap(prev => {
        const m = new Map(prev)
        m.set(author.username, { requestId: res.data.requestId ?? null })
        return m
      })
      // Don't remove from suggestions list yet — user might cancel; keep card visible.
      // Don't bump followingCount — not following until accept.
      // Don't refresh feed — they're not following yet.
    } else {
      setFollowingSet(prev => new Set([...prev, author.username]))
      setSuggestions(prev => prev.filter(a => a.username !== author.username))
      setFollowingCount(prev => (prev ?? 0) + 1)
      refreshFeed()
    }
  }

  async function unfollow(username) {
    const headers = await getHeaders()
    await axios.delete(`/api/follows/${username}`, { headers })
    setFollowingSet(prev => { const s = new Set(prev); s.delete(username); return s })
    setFollowingCount(prev => Math.max(0, (prev ?? 1) - 1))
    refreshFeed()
  }

  async function cancelRequest(username, requestId) {
    const headers = await getHeaders()
    if (requestId) {
      await axios.delete(`/api/follow-requests/${requestId}`, { headers })
    }
    setRequestedMap(prev => {
      const m = new Map(prev)
      m.delete(username)
      return m
    })
  }

  async function handleWallToggleLike(postId) {
    const wasLiked = likedWallPosts.has(postId)
    setLikedWallPosts(prev => { const s = new Set(prev); wasLiked ? s.delete(postId) : s.add(postId); return s })
    setWallPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: (p.likes_count ?? 0) + (wasLiked ? -1 : 1) } : p))
    try {
      const headers = await getHeaders()
      const { data } = await axios.post(`/api/wall/posts/${postId}/like`, {}, { headers })
      setLikedWallPosts(prev => { const s = new Set(prev); data.liked ? s.add(postId) : s.delete(postId); return s })
      setWallPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: data.likes_count } : p))
    } catch {
      setLikedWallPosts(prev => { const s = new Set(prev); wasLiked ? s.add(postId) : s.delete(postId); return s })
      setWallPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: (p.likes_count ?? 0) + (wasLiked ? 1 : -1) } : p))
    }
  }

  function handleWallCommentsCountChange(postId, delta) {
    setWallPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, comments_count: Math.max(0, (p.comments_count ?? 0) + delta) } : p
    ))
  }

  async function refreshFeed() {
    try {
      const headers = await getHeaders()
      const { data } = await axios.get('/api/follows/feed', { params: { limit: PAGE, offset: 0 }, headers })
      setStories(data.stories)
      setTotal(data.total)
      setFollowingCount(data.followingCount)
      setOffset(data.stories.length)
    } catch (err) { console.error('[FeedPage] Failed to refresh feed:', err) }
  }

  const progressMap = Object.fromEntries(
    continueReading.map(s => [s.id, Math.round((s.currentChapter / s.chapters) * 100)])
  )

  // Not logged in
  if (!user) {
    return (
      <div className="feed-page">
        <Navbar />
        <div className="feed-gate">
          <div className="feed-gate__icon">📖</div>
          <h1 className="feed-gate__title">Your personalised feed</h1>
          <p className="feed-gate__desc">
            Follow authors you love to see their latest stories here, curated just for you.
          </p>
          <div className="feed-gate__actions">
            <Link to="/signup" className="btn btn-primary">Create an account</Link>
            <Link to="/login"  className="btn btn-outline">Sign in</Link>
          </div>
        </div>
      </div>
    )
  }

  const hasMore = stories.length < total

  return (
    <div className="feed-page">
      <SEO title="Your Feed" description="Your personalized Verse feed — stories and updates from writers you follow." noindex />
      <Navbar />

      <PullToRefresh onRefresh={loadFeed}>
      <div className="feed-page__inner">
        {/* ── Main column ── */}
        <main className="feed-main">
          <div className="feed-main__header">
            <div>
              <h1 className="feed-main__title">Your Feed</h1>
              <p className="feed-main__subtitle">
                {followingCount === null
                  ? 'Loading…'
                  : followingCount === 0
                  ? 'Follow some authors to see their stories and quizzes here'
                  : 'Stories and quizzes from authors you follow'}
              </p>
            </div>
            {followingCount > 0 && (
              <span className="feed-main__count">{formatNum(total)} posts</span>
            )}
          </div>

          {/* Continue Reading */}
          {!loading && continueReading.length > 0 && (
            <div className="continue-reading">
              <h2 className="continue-reading__title">Continue Reading</h2>
              <div className="continue-reading__list">
                {continueReading.map(story => (
                  <Link
                    key={story.id}
                    to={`/story/${story.id}?chapter=${story.currentChapter}`}
                    className="continue-reading__card"
                  >
                    <div className="continue-reading__cover">
                      {story.bannerUrl || story.coverUrl
                        ? <img src={story.bannerUrl || story.coverUrl} alt="" loading="lazy" />
                        : <div className="continue-reading__cover-grad" style={{ background: story.authorAvatarGrad || 'linear-gradient(135deg, #7C3AED, #EC4899)' }} />
                      }
                    </div>
                    <div className="continue-reading__right">
                      <div className="continue-reading__info">
                        <span className="continue-reading__name">{decodeEntities(story.title)}{story.sexuallyExplicit && <span className="explicit-inline-badge">18+</span>}</span>
                        <span className="continue-reading__meta">
                          Ch. {story.currentChapter} of {story.chapters} · {decodeEntities(story.author)}
                        </span>
                      </div>
                      <div className="continue-reading__bar">
                        <div
                          className="continue-reading__bar-fill"
                          style={{ width: `${Math.round((story.currentChapter / story.chapters) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Online mutuals — mobile placement (≤900px); hidden on desktop via CSS */}
          <OnlineMutuals mutuals={onlineMutuals} variant="inline" />

          {/* Recent wall posts */}
          {!loading && wallPosts.length > 0 && (
            <div className="feed-wall-section">
              <h2 className="feed-wall-section__title">Recent from your feed</h2>
              <div className="wall-list">
                {wallPosts.map(post => {
                  const liked = likedWallPosts.has(post.id)
                  const avatarStyle = { background: post.authorAvatarUrl ? undefined : (post.authorAvatarGrad ?? 'linear-gradient(135deg, #7C3AED, #EC4899)') }
                  const wallUrl = `/user/${post.authorUsername}?tab=wall`
                  return (
                    <div key={post.id} className="wall-post">
                      <div className="wall-post__header">
                        <Link to={`/user/${post.authorUsername}`} className="wall-post__avatar" style={avatarStyle}>
                          {post.authorAvatarUrl
                            ? <img src={post.authorAvatarUrl} alt={post.authorDisplayName ?? post.authorUsername} />
                            : (post.authorDisplayName ?? post.authorUsername)?.[0]?.toUpperCase() ?? '?'}
                        </Link>
                        <div className="wall-post__meta">
                          <Link to={`/user/${post.authorUsername}`} className="wall-post__author">
                            {post.authorDisplayName ?? post.authorUsername}
                          </Link>
                          <div className="wall-post__time">{wallTimeAgo(post.created_at)}</div>
                        </div>
                        {user && user.id !== post.user_id && (
                          <button className="wall-post__report" onClick={() => setReportingPostId(post.id)} title="Report" aria-label="Report">⚑</button>
                        )}
                      </div>
                      <Link to={wallUrl} className="wall-post__content" style={{ maxHeight: 80, overflow: 'hidden', display: 'block', textDecoration: 'none', color: 'inherit' }}>
                        {renderMentions(post.content?.length > 200 ? post.content.slice(0, 200) + '…' : post.content)}
                      </Link>
                      <div className="wall-post__actions">
                        <button
                          type="button"
                          className={`wall-post__like${liked ? ' wall-post__like--active' : ''}`}
                          onClick={() => handleWallToggleLike(post.id)}
                        >
                          {liked ? '❤️' : '🤍'} {post.likes_count ?? 0}
                        </button>
                        <button
                          type="button"
                          className={`wall-post__comment-toggle${openComments.has(post.id) ? ' wall-post__comment-toggle--active' : ''}`}
                          onClick={() => setOpenComments(prev => {
                            const s = new Set(prev)
                            s.has(post.id) ? s.delete(post.id) : s.add(post.id)
                            return s
                          })}
                        >
                          💬 {post.comments_count ?? 0}
                        </button>
                      </div>
                      {openComments.has(post.id) && (
                        <WallComments
                          postId={post.id}
                          user={user}
                          isAdmin={false}
                          onCountChange={delta => handleWallCommentsCountChange(post.id, delta)}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          {reportingPostId && (
            <ReportModal contentType="wall_post" contentId={reportingPostId} onClose={() => setReportingPostId(null)} />
          )}

          {loading ? (
            <SkeletonLoader type="story-grid" />
          ) : followingCount === 0 ? (
            <EmptyFeed />
          ) : stories.length === 0 ? (
            <div className="feed-empty-stories">
              <p>The authors you follow haven't posted any stories or quizzes yet.</p>
              <p>Check back soon, or discover more authors in the sidebar.</p>
            </div>
          ) : (
            <>
              <div className="story-grid story-grid--dense">
                {stories.map((story, i) => (
                  <StoryCard key={story.id} story={story} index={i} progress={progressMap[story.id] ?? null} />
                ))}
              </div>

              {hasMore && (
                <div className="feed-load-more">
                  <button
                    className="btn btn-outline"
                    onClick={loadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Loading…' : 'Load more stories'}
                  </button>
                </div>
              )}
            </>
          )}
        </main>

        {/* ── Sidebar ── */}
        <aside className="feed-sidebar">
          {/* Desktop placement (>900px); hidden on mobile via CSS */}
          <OnlineMutuals mutuals={onlineMutuals} variant="sidebar" />
          {suggestions.length > 0 && (
            <div className="feed-sidebar__section">
              <div className="feed-sidebar__title-row">
                <h2 className="feed-sidebar__title">
                  {followingCount === 0 ? 'Authors to follow' : 'Discover authors'}
                </h2>
                <button
                  type="button"
                  className="feed-sidebar__refresh-btn"
                  onClick={refreshSuggestions}
                  disabled={refreshingSuggestions}
                  aria-label="Refresh suggestions"
                  title="Refresh suggestions"
                >
                  <RefreshIcon spinning={refreshingSuggestions} />
                </button>
              </div>
              <p className="feed-sidebar__subtitle">
                {followingCount === 0
                  ? 'Get started by following a few writers'
                  : 'You might enjoy these writers'}
              </p>
              <div className="feed-author-list">
                {suggestions.map(author => (
                  <AuthorCard
                    key={author.username}
                    author={author}
                    onFollow={follow}
                    onUnfollow={unfollow}
                    onCancelRequest={cancelRequest}
                    followingSet={followingSet}
                    requestedMap={requestedMap}
                  />
                ))}
              </div>
              <Link to="/browse" className="feed-sidebar__browse-link">
                Browse all stories →
              </Link>
            </div>
          )}

          <div className="feed-sidebar__section feed-sidebar__tips">
            <h2 className="feed-sidebar__title">Finding great stories</h2>
            <ul className="feed-tips-list">
              <li><Link to="/browse">Browse</Link> by genre to find authors you love</li>
              <li>Visit an author's profile and hit <strong>+ Follow</strong></li>
              <li>Stories from people you follow appear here instantly</li>
              <li><Link to="/quizzes">Take quizzes</Link> to discover fandoms</li>
            </ul>
          </div>
        </aside>
      </div>
      </PullToRefresh>
      <BackToTop />
    </div>
  )
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyFeed() {
  return (
    <div className="feed-empty">
      <div className="feed-empty__illustration">
        <div className="feed-empty__ring feed-empty__ring--1" />
        <div className="feed-empty__ring feed-empty__ring--2" />
        <div className="feed-empty__ring feed-empty__ring--3" />
        <span className="feed-empty__icon">✨</span>
      </div>
      <h2 className="feed-empty__title">Your feed is empty</h2>
      <p className="feed-empty__desc">
        Start following authors to see their latest stories appear here.
        Check out the suggestions on the right to get started.
      </p>
      <div className="feed-empty__actions">
        <Link to="/browse" className="btn btn-primary">Discover stories</Link>
        <Link to="/quizzes" className="btn btn-outline">Take a quiz</Link>
      </div>
    </div>
  )
}
